/**
 * Resources the FiveM server brings with it.
 *
 * A `dependency 'chat'` line names something no author ever ships: `chat`,
 * `spawnmanager`, `mapmanager` and the rest arrive with the server artifacts as
 * part of `cfx-server-data`. A dependency checker that only looked at the folder
 * it was handed would call every one of them missing, which is the single
 * loudest false positive in this whole area — and the reason a resource that
 * works perfectly gets a red report.
 *
 * So they resolve to `system`: present, not yours, and not a fault.
 *
 * Casing is not consistent even in Rockstar-adjacent code — `gameInit` is
 * camel-cased and everybody writes it `gameinit` — so lookups go through the
 * normalized set and the display name is kept separately for anything that has
 * to render one.
 */

/** Canonical spelling, in the casing the FiveM docs use. */
export const SERVER_PROVIDED_RESOURCES: readonly string[] = [
  "baseevents",
  "basic-gamemode",
  "cfx-server-data",
  "chat",
  "example-loadscreen",
  "fivem",
  "fivem-map-hipster",
  "fivem-map-skater",
  "gameInit",
  "hardcap",
  "mapmanager",
  "monitor",
  "player_data",
  "playernames",
  "rconlog",
  "redm-map-one",
  "runcode",
  "scoreboard",
  "sessionmanager",
  "spawnmanager",
  "webadmin",
  "webpack",
  "yarn",
];

/** Lower-cased, for lookups. Build once; every call site shares it. */
const BY_NORMALIZED = new Map(
  SERVER_PROVIDED_RESOURCES.map((name) => [name.toLowerCase(), name] as const),
);

export function isServerProvidedResource(name: string): boolean {
  return BY_NORMALIZED.has(name.trim().toLowerCase());
}

/**
 * The canonical spelling of a server-provided resource, or `null`.
 *
 * Findings say `gameInit` even when the manifest said `gameinit`, because the
 * point of naming it is that the reader can go and look it up.
 */
export function serverProvidedResourceName(name: string): string | null {
  return BY_NORMALIZED.get(name.trim().toLowerCase()) ?? null;
}
